import { create } from 'zustand';

/**
 * 튜토리얼 페이지 타입
 */
export type TutorialPage = 'my-space' | 'space-management' | 'account-settings';

/**
 * 페이지별 라우트 매핑
 */
export const TUTORIAL_ROUTES: Record<TutorialPage, string> = {
  'my-space': '/my-space',
  'space-management': '/space-management',
  'account-settings': '/settings',
};

/**
 * 페이지별 이름 매핑
 */
export const TUTORIAL_PAGE_NAMES: Record<TutorialPage, string> = {
  'my-space': '마이스페이스',
  'space-management': '회사 관리',
  'account-settings': '계정 설정',
};

/**
 * 튜토리얼 순서
 */
export const TUTORIAL_ORDER: TutorialPage[] = ['my-space', 'space-management', 'account-settings'];

/**
 * localStorage 키
 */
const STORAGE_KEYS = {
  ONBOARDING_FLOW: 'factsheet_onboarding_flow', // 처음 가입 플로우 진행 중인지
  COMPLETED_TUTORIALS: 'factsheet_completed_tutorials', // 완료된 튜토리얼 목록
};

/**
 * 튜토리얼 상태 인터페이스
 */
interface TutorialState {
  /** 처음 가입 플로우 진행 중인지 */
  isOnboardingFlow: boolean;
  /** 완료된 튜토리얼 페이지 목록 */
  completedTutorials: TutorialPage[];
  /** 현재 실행 중인 튜토리얼 페이지 */
  activeTutorial: TutorialPage | null;
}

/**
 * 튜토리얼 액션 인터페이스
 */
interface TutorialActions {
  /** 온보딩 플로우 시작 (처음 가입 시) */
  startOnboardingFlow: () => void;
  /** 특정 페이지 튜토리얼 시작 */
  startTutorial: (page: TutorialPage) => void;
  /** 현재 튜토리얼 완료 및 다음 페이지로 */
  completeTutorialAndGoNext: () => TutorialPage | null;
  /** 현재 튜토리얼만 완료 (다음으로 안 넘어감) */
  completeTutorial: (page: TutorialPage) => void;
  /** 튜토리얼 중지 */
  stopTutorial: () => void;
  /** 특정 페이지 튜토리얼이 완료되었는지 확인 */
  isTutorialCompleted: (page: TutorialPage) => boolean;
  /** 모든 튜토리얼 완료 여부 확인 */
  isAllTutorialsCompleted: () => boolean;
  /** 특정 페이지 튜토리얼 초기화 (다시 보기용) */
  resetTutorial: (page: TutorialPage) => void;
  /** 모든 튜토리얼 초기화 */
  resetAllTutorials: () => void;
  /** localStorage에서 상태 로드 */
  loadFromStorage: () => void;
  /** 다음 튜토리얼 페이지 가져오기 */
  getNextTutorialPage: () => TutorialPage | null;
}

/**
 * 튜토리얼 상태 관리 store
 */
export const useTutorialStore = create<TutorialState & TutorialActions>((set, get) => ({
  // 초기 상태
  isOnboardingFlow: false,
  completedTutorials: [],
  activeTutorial: null,

  // 온보딩 플로우 시작
  startOnboardingFlow: () => {
    set({
      isOnboardingFlow: true,
      activeTutorial: 'my-space',
      completedTutorials: [],
    });
    localStorage.setItem(STORAGE_KEYS.ONBOARDING_FLOW, 'true');
    localStorage.setItem(STORAGE_KEYS.COMPLETED_TUTORIALS, JSON.stringify([]));
  },

  // 특정 페이지 튜토리얼 시작
  startTutorial: (page: TutorialPage) => {
    set({ activeTutorial: page });
  },

  // 현재 튜토리얼 완료 및 다음 페이지로
  completeTutorialAndGoNext: () => {
    const { activeTutorial, completedTutorials, isOnboardingFlow } = get();
    
    if (!activeTutorial) return null;

    // 현재 튜토리얼을 완료 목록에 추가
    const newCompleted = [...completedTutorials];
    if (!newCompleted.includes(activeTutorial)) {
      newCompleted.push(activeTutorial);
    }

    // 다음 페이지 찾기
    const currentIndex = TUTORIAL_ORDER.indexOf(activeTutorial);
    const nextIndex = currentIndex + 1;
    
    if (isOnboardingFlow && nextIndex < TUTORIAL_ORDER.length) {
      const nextPage = TUTORIAL_ORDER[nextIndex];
      set({
        completedTutorials: newCompleted,
        activeTutorial: null, // 페이지 이동 후 해당 페이지에서 시작
      });
      localStorage.setItem(STORAGE_KEYS.COMPLETED_TUTORIALS, JSON.stringify(newCompleted));
      return nextPage;
    } else {
      // 모든 튜토리얼 완료
      set({
        completedTutorials: newCompleted,
        activeTutorial: null,
        isOnboardingFlow: false,
      });
      localStorage.setItem(STORAGE_KEYS.COMPLETED_TUTORIALS, JSON.stringify(newCompleted));
      localStorage.removeItem(STORAGE_KEYS.ONBOARDING_FLOW);
      return null;
    }
  },

  // 현재 튜토리얼만 완료
  completeTutorial: (page: TutorialPage) => {
    const { completedTutorials } = get();
    const newCompleted = [...completedTutorials];
    if (!newCompleted.includes(page)) {
      newCompleted.push(page);
    }
    set({
      completedTutorials: newCompleted,
      activeTutorial: null,
    });
    localStorage.setItem(STORAGE_KEYS.COMPLETED_TUTORIALS, JSON.stringify(newCompleted));
  },

  // 튜토리얼 중지
  stopTutorial: () => {
    set({ activeTutorial: null });
  },

  // 특정 페이지 튜토리얼이 완료되었는지 확인
  isTutorialCompleted: (page: TutorialPage) => {
    return get().completedTutorials.includes(page);
  },

  // 모든 튜토리얼 완료 여부 확인
  isAllTutorialsCompleted: () => {
    const { completedTutorials } = get();
    return TUTORIAL_ORDER.every(page => completedTutorials.includes(page));
  },

  // 특정 페이지 튜토리얼 초기화 (다시 보기용)
  resetTutorial: (page: TutorialPage) => {
    const { completedTutorials } = get();
    const newCompleted = completedTutorials.filter(p => p !== page);
    set({
      completedTutorials: newCompleted,
      activeTutorial: page,
      isOnboardingFlow: false, // 다시 보기는 온보딩 플로우가 아님
    });
    localStorage.setItem(STORAGE_KEYS.COMPLETED_TUTORIALS, JSON.stringify(newCompleted));
  },

  // 모든 튜토리얼 초기화
  resetAllTutorials: () => {
    set({
      isOnboardingFlow: false,
      completedTutorials: [],
      activeTutorial: null,
    });
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_FLOW);
    localStorage.removeItem(STORAGE_KEYS.COMPLETED_TUTORIALS);
  },

  // localStorage에서 상태 로드
  loadFromStorage: () => {
    const onboardingFlow = localStorage.getItem(STORAGE_KEYS.ONBOARDING_FLOW);
    const completedStr = localStorage.getItem(STORAGE_KEYS.COMPLETED_TUTORIALS);
    
    let completedTutorials: TutorialPage[] = [];
    if (completedStr) {
      try {
        completedTutorials = JSON.parse(completedStr);
      } catch (e) {
        console.error('Failed to parse completed tutorials:', e);
      }
    }

    set({
      isOnboardingFlow: onboardingFlow === 'true',
      completedTutorials,
    });
  },

  // 다음 튜토리얼 페이지 가져오기
  getNextTutorialPage: () => {
    const { activeTutorial } = get();
    if (!activeTutorial) return null;

    const currentIndex = TUTORIAL_ORDER.indexOf(activeTutorial);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < TUTORIAL_ORDER.length) {
      return TUTORIAL_ORDER[nextIndex];
    }
    return null;
  },
}));

